import { useCallback, useState } from 'react';
import { VisibilityLayers } from '../../../../../types/enums/VisibilityLayers';
import { WithConfigurationCanvas } from '../../../configuration-canvas/WithConfigurationCanvas';
import { VisibilityElementLayersSelector } from '../../../configuration-canvas/types';
import { CanvasUnitedFlow } from './CanvasUnitedFlow';
import {
  CanvasPillTypes,
  GroupLabelEntityType,
  GroupPillEntityType,
  NodeEntityTypes,
  StepEntityType,
  StepPillEntityType,
  SweepCanvasEntities,
} from '../../../../sweep-canvas/canvas-types';
import { usePanels } from '../../../../panels/usePanels';
import { documentationPanelType } from '../../../../documentation/types';
import { Box } from '@mui/material';
import { DEFAULT_LAYERS_ID } from '../../../../../reducers/canvasLayersReducer';
import { useConfigurationCanvas } from '../../../configuration-canvas/public/useConfigurationCanvas';
import { useSelector } from 'react-redux';
import { selectStageDialogStage } from '../../../../../reducers/funnel-flow-page/stageDialogReducer';
import { funnelPanelType } from '../../../funnel-map-flow/dialogs/funnel-panel/funnelPanelTypes';
import { gatePanelType } from '../../../funnel-map-flow/dialogs/gates/gatePanelType';
import { useInitializeObjectsStructure } from '../../../../documentation/hooks/useInitializeObjectsStructure';
import useOpenStepDialog from '../useOpenStageDialog';
import {
  DocumentationContextProvider,
  useDocumentationContext,
} from '../../../../documentation/context/DocumentationContext';
import { useFocusOnCanvasElements } from '../useFocusOnCanvasElements';
import { useChangeEnvironment } from '../../../../../hooks/useChangeEnvironment';
import { useSweepNotifications } from '../../../../notifications/useSweepNotifications';
import { SweepNotificationVariant } from '../../../../../reducers/notificationsReducer';
import { telemetry } from '../../../../../telemetry';

const visibilityElementsLayerSelector: VisibilityElementLayersSelector = {
  type: 'layersSelector',
  layersId: DEFAULT_LAYERS_ID,
  visibilityLayersSelectors: {
    [VisibilityLayers.SWEEP_AUTOMATIONS]: true,
    [VisibilityLayers.NURTURING_STEPS]: true,
    [VisibilityLayers.COMMENTS]: true,
    [VisibilityLayers.VALIDATION_RULES]: true,
    [VisibilityLayers.SFDC_AUTOMATIONS]: true,
    [VisibilityLayers.APEX]: true,
    [VisibilityLayers.PLAYBOOK_ALERTS]: true,
    [VisibilityLayers.ASSIGNMENT_RULES]: true,
    [VisibilityLayers.FUNNEL_MATCHING_AND_DEDUPLICATION]: true,
    [VisibilityLayers.GATES]: true,
    [VisibilityLayers.ENVIRONMENTS]: false,
  },
  defaultVisibilityMap: {
    [VisibilityLayers.NURTURING_STEPS]: true,
    [VisibilityLayers.COMMENTS]: true,
    [VisibilityLayers.SFDC_AUTOMATIONS]: true,
    [VisibilityLayers.APEX]: true,
    [VisibilityLayers.VALIDATION_RULES]: true,
    [VisibilityLayers.GATES]: true,
    [VisibilityLayers.ENVIRONMENTS]: false,
  },
};

const documentationPills = [
  CanvasPillTypes.apexTrigger,
  CanvasPillTypes.groupApex,
  CanvasPillTypes.sfdcAutomation,
  CanvasPillTypes.groupSfdcAutomation,
  CanvasPillTypes.validationRule,
  CanvasPillTypes.groupValidationRule,
  CanvasPillTypes.groupDeduplication,
  CanvasPillTypes.groupMatching,
  CanvasPillTypes.assignmentRule,
  CanvasPillTypes.groupAssignmentRule,
  CanvasPillTypes.scheduledAssignment,
  CanvasPillTypes.groupScheduledAssignment,
  CanvasPillTypes.automation,
  CanvasPillTypes.groupAutomation,
  CanvasPillTypes.playbookAlert,
  CanvasPillTypes.groupPlaybookAlert,
];

const isDocumentationPill = (entity: SweepCanvasEntities) => {
  if ('canvasPillType' in entity) {
    return documentationPills.includes(entity.canvasPillType);
  }
  return false;
};

const useOpenFunnelDialog = () => {
  const { maybeOpenPanel } = usePanels();

  const openFunnelDialog = useCallback(
    ({ funnelId }: { funnelId: string }) => {
      maybeOpenPanel({
        panelType: funnelPanelType,
        data: {
          funnelId,
        },
      });
    },
    [maybeOpenPanel],
  );
  return {
    openFunnelDialog,
  };
};

const CanvasUnitedConfigurationPage = () => {
  const { maybeOpenPanel } = usePanels();
  const { canvasCrmOrgId, canvasFunnelMap } = useConfigurationCanvas();
  const { openStepDialog } = useOpenStepDialog();
  const { openFunnelDialog } = useOpenFunnelDialog();
  const { setEntityClicked } = useDocumentationContext();
  const { changeEnvironment } = useChangeEnvironment();
  const { addNotification } = useSweepNotifications();

  //required for documentation filters
  useInitializeObjectsStructure({
    crmOrgId: canvasCrmOrgId,
    funnelsData: canvasFunnelMap.funnelsData,
    recordTypesData: canvasFunnelMap.recordTypesData,
  });

  const [selectedGateId, setSelectedGateId] = useState<string>();

  const onEntityClick = useCallback(
    ({
      entity,
      event,
      funnelId,
      stepId,
    }: {
      stepId?: string;
      funnelId: string;
      entity: StepEntityType | StepPillEntityType | GroupPillEntityType | GroupLabelEntityType;
      event: React.MouseEvent | React.TouchEvent;
      isLongClick?: boolean;
    }) => {
      if (entity.type === NodeEntityTypes.Step && stepId && funnelId) {
        openStepDialog({ stepId, funnelId });
      }

      if (entity.type === NodeEntityTypes.GroupLabel) {
        openFunnelDialog({ funnelId });
      }
      if (isDocumentationPill(entity)) {
        const funnel = canvasFunnelMap.funnelsData?.[funnelId];
        const pillCrmOrgId = funnel?.crmOrgId;

        // If funnel's CRM org ID is not available, show error and return early
        if (!pillCrmOrgId) {
          const errorMessage = 'Unable to determine CRM org for this funnel. Please try again.';
          addNotification({
            message: errorMessage,
            variant: SweepNotificationVariant.Error,
          });
          telemetry.captureError(new Error('Funnel crmOrgId is missing when clicking pill'), {
            funnelId,
            entityType: entity.type,
            canvasPillType: 'canvasPillType' in entity ? entity.canvasPillType : undefined,
            stepId,
          });
          return;
        }

        if (pillCrmOrgId !== canvasCrmOrgId) {
          changeEnvironment(pillCrmOrgId);
        }

        maybeOpenPanel({
          panelType: documentationPanelType,
          onOpen: () => {
            setEntityClicked({
              stepId: stepId,
              funnelId: funnelId,
              entity: entity,
              event: event,
              funnelsData: canvasFunnelMap.funnelsData,
              recordTypesData: canvasFunnelMap.recordTypesData,
            });
          },
        });
      }
    },
    [
      openStepDialog,
      setEntityClicked,
      openFunnelDialog,
      maybeOpenPanel,
      canvasFunnelMap.funnelsData,
      canvasFunnelMap.recordTypesData,
      canvasCrmOrgId,
      changeEnvironment,
      addNotification,
    ],
  );

  const onStageGateClick = useCallback(
    (funnelId: string, stageId: string, exitCriteriaId: string) => {
      maybeOpenPanel({
        panelType: gatePanelType,
        onOpen: () => {
          setSelectedGateId(exitCriteriaId);
        },
        onClose: () => {
          setSelectedGateId(undefined);
        },
        data: {
          funnelId,
          stageId,
          exitCriteriaId,
        },
      });
    },
    [maybeOpenPanel],
  );

  const selectedStageId = useSelector(selectStageDialogStage)?._stageId;
  const { isPanelOpen, getPanelDataIfOpened } = usePanels();
  const _selectedGateId = isPanelOpen(gatePanelType) ? selectedGateId : undefined;
  const _selectedFunnelId = getPanelDataIfOpened(funnelPanelType)?.funnelId;

  return (
    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: '100%' }}>
      <WithConfigurationCanvas
        visibilityElements={visibilityElementsLayerSelector}
        onFunnelStepClick={onEntityClick}
        onFunnelLabelClick={onEntityClick}
        onFunnelLabelPillClick={onEntityClick}
        onStageGateClick={onStageGateClick}
        selectedGateId={_selectedGateId}
        selectedStageId={selectedStageId}
        selectedFunnelId={_selectedFunnelId}
      >
        <CanvasUnitedFlow />
      </WithConfigurationCanvas>
    </Box>
  );
};

const CanvasUnitedConfigurationPageWithProviders = () => {
  const { canvasFunnelMap, canvasCrmOrgId } = useConfigurationCanvas();
  const focusOnCanvasElements = useFocusOnCanvasElements(canvasFunnelMap);

  return (
    <DocumentationContextProvider
      crmOrgId={canvasCrmOrgId}
      focusOnCanvasElements={focusOnCanvasElements}
      canvasFunnelMap={canvasFunnelMap}
    >
      <CanvasUnitedConfigurationPage />
    </DocumentationContextProvider>
  );
};

export { CanvasUnitedConfigurationPageWithProviders as CanvasUnitedConfigurationPage };
